import { useEffect } from 'react';

import { Route, Routes, useParams } from 'react-router-dom';

import { NewKimThread } from './:threadId';
import { VerificationViewer } from './:threadId/components/verification-viewer';
import { NewKimLargeInput } from './components/new-kim-large-input';
import { NewKimSidebar } from './components/new-kim-sidebar';
import { KimArtifacts } from './work-product';
import { Permission } from '../../../../../../../../../backend/src/shared/action-registry';
import { useGetMatterV2 } from '@/api/queries/useGetMatterV2';
import { useGetSubset } from '@/api/queries/useGetSubset';
import { useHasPermission } from '@/hooks/useHasPermission';
import { Breadcrumb } from '@/new/components/breadcrumb';
import { Icon } from '@/new/components/icon';
import { Empty, EmptyHeader, EmptyMedia, EmptyTitle, EmptyDescription } from '@/new/components/shadcn/empty';
import { useSidebar } from '@/new/components/shadcn/sidebar';
import { useSubsetBreadcrumbs } from '@/new/hooks/use-subset-breadcrumbs';
import { useMatterProcessingStore } from '@/new/stores/matter-processing-store';

export function NewKim() {
  const { matterId, subsetId } = useParams();
  const { data: matter } = useGetMatterV2(matterId);
  const { data: subset } = useGetSubset(matterId, subsetId);
  const { setOpen } = useSidebar();

  const breadcrumbItems = useSubsetBreadcrumbs({
    matterId: matterId ?? '',
    subsetId: subsetId ?? '',
    matter,
    subset,
    currentPage: { label: 'Kim', path: 'new-kim' },
  });

  // Close the sidebar when visiting new kim
  useEffect(() => setOpen(false), []);

  const { hasPermission } = useHasPermission();
  const canCreateThread = hasPermission(Permission.ThreadCreate);

  const { getProcessingState } = useMatterProcessingStore();

  if (!matterId) return null;

  if (getProcessingState(matterId)?.hasCompletedRuns === false) {
    return (
      <Empty>
        <EmptyHeader>
          <EmptyMedia variant="icon">
            <Icon className="lucide--bot" />
          </EmptyMedia>
          <EmptyTitle>Kim Unavailable</EmptyTitle>
          <EmptyDescription>Kim is unavailable while documents are still processing.</EmptyDescription>
        </EmptyHeader>
      </Empty>
    );
  }

  return (
    <>
      <Breadcrumb items={breadcrumbItems} />
      <div className="flex h-full w-full overflow-hidden bg-gray-50">
        {/* Sidebar */}
        <NewKimSidebar />

        {/* Main content */}
        <div className="flex grow items-center justify-center">
          <Routes>
            <Route
              index
              element={
                canCreateThread ? (
                  <NewKimLargeInput />
                ) : (
                  <Empty className="mb-16">
                    <EmptyHeader>
                      <EmptyMedia variant="icon">
                        <Icon className="lucide--lock" />
                      </EmptyMedia>
                      <EmptyTitle>Permission Required</EmptyTitle>
                      <EmptyDescription>
                        You don't have permission to create new threads. Speak to an administrator to gain access.
                      </EmptyDescription>
                    </EmptyHeader>
                  </Empty>
                )
              }
            />
            <Route path="work-product" element={<KimArtifacts />} />
            <Route path=":threadId/verify/:blockId" element={<VerificationViewer />} />
            <Route path=":threadId" element={<NewKimThread />} />
          </Routes>
        </div>
      </div>
    </>
  );
}
